import React, { FC, useState, useCallback, useRef } from 'react';
import Reviews from '@components/Reviews';
import { Box, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import dynamic from 'next/dynamic';
import { ACTIONS } from '@components/Contexts/TemplateContext';
import ReviewUnavailableNotification from 'components/Template/TemplateReviews/ReviewUnavailableNotification';
import { useTemplateReviewsLogic } from './hooks/useTemplateReviewsLogic';
import { useTemplateReviewsConfig } from './hooks/useTemplateReviewsConfig';

const REVIEWS_PAGE_SIZE = 15;

const DynamicTemplateReviewModal = dynamic(
  () => import('@components/Reviews/ReviewModal/TemplateReviewModal'),
);

type TemplateReviewsProps = {
  isMobileView: boolean;
  showReviewsCount: boolean;
};

const TemplateReviews: FC<TemplateReviewsProps> = ({
  isMobileView,
  showReviewsCount,
}) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(REVIEWS_PAGE_SIZE);
  const toggleButtonRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback((shouldShowMore: boolean) => {
    if (shouldShowMore) {
      setVisibleCount((prev) => prev + REVIEWS_PAGE_SIZE);
    } else {
      setVisibleCount(REVIEWS_PAGE_SIZE);
      setTimeout(() => {
        const el = toggleButtonRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          window.scrollTo({
            top: window.scrollY + rect.bottom - window.innerHeight + 60,
            behavior: 'smooth',
          });
        }
      }, 0);
    }
  }, []);

  // Extract all business logic
  const logic = useTemplateReviewsLogic(isMobileView);

  // Extract all configuration
  const { contentProps, infiniteScrollProps } = useTemplateReviewsConfig({
    ...logic,
    isMobileView,
    showReviewsCount,
    visibleCount,
  });

  const {
    template,
    onSubmitSuccess,
    handleBiGenericClick,
    showErrorNotification,
    setShowErrorNotification,
    dispatch,
  } = logic;

  return (
    <Box dataHook="template-reviews" width="100%">
      <DynamicTemplateReviewModal
        id={template.id}
        templateName={template.title}
        businessName={template.partnerProfileDetails.businessName}
        templateMediaSources={template.media.additionalImages}
        onSubmitSuccess={onSubmitSuccess}
        isMobileView={isMobileView}
        onClose={() => {
          dispatch({
            type: ACTIONS.REVIEW_MODAL_AUTO_FOCUS,
            reviewModalAutoFocus: false,
          });
        }}
        handleBiGenericClick={handleBiGenericClick}
      />
      <Box direction="vertical">
        <Reviews
          paginationMode="infiniteScroll"
          infiniteScrollProps={infiniteScrollProps}
          content={contentProps}
        />
        {logic.reviewsInfo.reviews.length > REVIEWS_PAGE_SIZE && (
          <Box
            padding={isMobileView ? '24px 0' : '24px 0 0 0'}
            ref={toggleButtonRef}
          >
            <TextButton
              skin="dark"
              underline="always"
              weight="thin"
              dataHook={
                visibleCount >= logic.reviewsInfo.reviews.length
                  ? 'show-less-button'
                  : 'show-more-button'
              }
              onClick={() =>
                handleToggle(visibleCount < logic.reviewsInfo.reviews.length)
              }
            >
              {visibleCount >= logic.reviewsInfo.reviews.length
                ? t('marketplace.template-page.review-show-less')
                : t('marketplace.template-page.review-show-more')}
            </TextButton>
          </Box>
        )}
      </Box>
      <ReviewUnavailableNotification
        show={showErrorNotification}
        onClose={() => setShowErrorNotification(false)}
        isMobileView={isMobileView}
      />
    </Box>
  );
};

export default TemplateReviews;
